import { Button } from '~/components/ui/button';
import type { GapAttribute } from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

import type { AspectRollup } from './gap-analysis.utils';
import { formatCompactVolume } from './gap-analysis.utils';
import type { GapTone } from './gap-legend';

interface Props {
  /** Already filtered by the caller — this component renders what it is given. */
  rollups: AspectRollup[];
  /** Rendered in the section header so the controls sit with what they drive. */
  filterBar: React.ReactNode;
  /** Distinguishes "this product has nothing" from "your filters match nothing". */
  isFiltered: boolean;
  onClearFilters: () => void;
}

const TONE_CLASS: Record<GapTone, string> = {
  had: 'border-border text-muted-foreground bg-background',
  restated: 'bg-muted-foreground/12 text-muted-foreground border-transparent',
  added: 'bg-success-50 border-success/35 text-success font-medium',
};

/** Screen-reader prefix, so the tone of a tag is never conveyed by colour alone. */
const TONE_PREFIX: Record<GapTone, string> = {
  had: '',
  restated: 'restated: ',
  added: 'new: ',
};

/** One attribute chip. Search volume rides inline rather than in a separate
 *  column, so the value of an addition is readable without cross-referencing. */
function AttributeTag({ row, tone, testId }: { row: GapAttribute; tone: GapTone; testId: string }) {
  const volume = row.searchVolume ?? 0;

  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex items-baseline gap-1.5 rounded-md border px-2.5 py-1 text-[12.5px] leading-5',
        TONE_CLASS[tone],
      )}
    >
      {tone !== 'had' ? (
        <span className="text-[11px] opacity-75" aria-hidden="true">
          {tone === 'added' ? '+' : '≈'}
        </span>
      ) : null}
      {TONE_PREFIX[tone] ? <span className="sr-only">{TONE_PREFIX[tone]}</span> : null}
      {row.tag}
      {volume > 0 ? (
        <span className="text-[10px] font-semibold tabular-nums opacity-75">
          {formatCompactVolume(volume)}/mo
        </span>
      ) : null}
    </span>
  );
}

/**
 * The comparison itself: one row per product aspect, the retailer's own
 * attributes on the left and the enriched set on the right. The reader never has
 * to hold two lists in their head — the layout has already done the comparing.
 */
export function GapAttributeDiff({ rollups, filterBar, isFiltered, onClearFilters }: Props) {
  return (
    <div className="px-5 pt-5 pb-1" data-testid="gap-analysis-diff">
      <div className="mb-3 flex flex-col gap-3">
        <div>
          <h3
            className="text-foreground text-[13px] leading-5 font-semibold"
            data-testid="gap-analysis-diff-title"
          >
            Attribute comparison
          </h3>
          <p className="text-muted-foreground mt-0.5 text-xs">
            Green attributes did not exist before enrichment — everything else was already there.
          </p>
        </div>
        {filterBar}
      </div>

      <div className="bg-background text-muted-foreground sticky top-0 z-[1] hidden grid-cols-[132px_minmax(0,1fr)_minmax(0,1.35fr)] gap-x-4 pt-3 pb-2 text-[10.5px] font-semibold tracking-wider uppercase lg:grid">
        <span>Category</span>
        <span>Before enrichment</span>
        <span className="text-success">After enrichment</span>
      </div>

      {rollups.map((rollup) => (
        <div
          key={rollup.aspect}
          data-testid={`gap-analysis-diff-row-${rollup.aspect}`}
          className="grid grid-cols-1 items-start gap-x-4 gap-y-2 border-t py-3 lg:grid-cols-[132px_minmax(0,1fr)_minmax(0,1.35fr)]"
        >
          <div className="flex flex-col gap-0.5 pt-0.5">
            <span className="text-[12.5px] font-semibold capitalize">{rollup.aspect}</span>
            <span
              data-testid={`gap-analysis-diff-new-count-${rollup.aspect}`}
              className={cn(
                'text-[10.5px] font-semibold',
                rollup.added.length > 0 ? 'text-success' : 'text-muted-foreground',
              )}
            >
              {rollup.added.length > 0 ? `${String(rollup.added.length)} new` : 'none new'}
            </span>
          </div>

          {rollup.had.length > 0 ? (
            <div
              className="flex min-w-0 flex-wrap gap-1.5"
              data-testid={`gap-analysis-diff-before-list-${rollup.aspect}`}
            >
              {rollup.had.map((row, index) => (
                <AttributeTag
                  key={`had-${row.tag}-${String(index)}`}
                  row={row}
                  tone="had"
                  testId={`gap-analysis-diff-had-badge-${rollup.aspect}-${row.tag}`}
                />
              ))}
            </div>
          ) : (
            // Mirrors the column header so the cell reads as an answer to it.
            <span
              className="text-muted-foreground self-start rounded-md border border-dashed px-2.5 py-1 text-xs"
              data-testid={`gap-analysis-diff-before-empty-${rollup.aspect}`}
            >
              No attributes before enrichment
            </span>
          )}

          <div
            className="flex min-w-0 flex-wrap gap-1.5"
            data-testid={`gap-analysis-diff-after-list-${rollup.aspect}`}
          >
            {[...rollup.restated, ...rollup.added].map((row, index) => (
              <AttributeTag
                key={`enriched-${row.tag}-${String(index)}`}
                row={row}
                tone={row.isNetNew === true ? 'added' : 'restated'}
                testId={`gap-analysis-diff-${row.isNetNew === true ? 'added' : 'restated'}-badge-${rollup.aspect}-${row.tag}`}
              />
            ))}
          </div>
        </div>
      ))}

      {rollups.length === 0 ? (
        <div
          className="flex flex-col items-center gap-2 border-t py-16 text-center"
          data-testid="gap-analysis-diff-empty"
        >
          <p className="text-foreground text-sm font-semibold">
            No attributes match these filters.
          </p>
          {isFiltered ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              className="cursor-pointer"
              data-testid="gap-analysis-diff-clear-filters-button"
            >
              Clear filters
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
